import { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";

export default function AuthModal() {
  const { authModalMode, closeAuth, login, signup } = useAuth();
  const open = Boolean(authModalMode);
  const [mode, setMode] = useState(authModalMode || "login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && authModalMode) {
      setMode(authModalMode);
      setError("");
    }
  }, [open, authModalMode]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeAuth();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeAuth]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (mode === "signup") {
        await signup({ name: name.trim(), email: email.trim(), password });
      } else {
        await login({ email: email.trim(), password });
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      data-testid="auth-modal"
    >
      <div
        className="absolute inset-0 bg-ink/70 backdrop-blur-sm"
        onClick={closeAuth}
      />
      <div className="relative w-full max-w-md bg-ivory border border-border shadow-2xl">
        <button
          onClick={closeAuth}
          className="absolute right-4 top-4 h-8 w-8 inline-flex items-center justify-center hover:bg-secondary transition-colors"
          data-testid="auth-close-btn"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-8 pb-6">
          <div className="text-[11px] uppercase tracking-[0.35em] text-muted-foreground">
            EliteStock
          </div>
          <h2 className="font-serif text-3xl mt-2 text-ink">
            {mode === "signup" ? "Create your account." : "Welcome back."}
          </h2>
          <p className="text-sm text-muted-foreground mt-2">
            {mode === "signup"
              ? "Save favorites, build collections, download in one click."
              : "Sign in to access your library."}
          </p>
        </div>

        <div className="flex px-8 gap-6 border-b border-border">
          <button
            onClick={() => { setMode("login"); setError(""); }}
            data-testid="auth-tab-login"
            className={`pb-3 text-sm tracking-wide transition-colors ${
              mode === "login"
                ? "border-b-2 border-ink text-ink -mb-px font-medium"
                : "text-muted-foreground hover:text-ink"
            }`}
          >
            Sign in
          </button>
          <button
            onClick={() => { setMode("signup"); setError(""); }}
            data-testid="auth-tab-signup"
            className={`pb-3 text-sm tracking-wide transition-colors ${
              mode === "signup"
                ? "border-b-2 border-ink text-ink -mb-px font-medium"
                : "text-muted-foreground hover:text-ink"
            }`}
          >
            Create account
          </button>
        </div>

        <form onSubmit={submit} className="p-8 space-y-4" data-testid="auth-form">
          {mode === "signup" && (
            <div>
              <label className="block text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1.5">
                Name
              </label>
              <input
                data-testid="auth-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                minLength={1}
                maxLength={80}
                className="w-full px-3 py-2.5 bg-white border border-border focus:border-ink outline-none text-sm"
              />
            </div>
          )}
          <div>
            <label className="block text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1.5">
              Email
            </label>
            <input
              data-testid="auth-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2.5 bg-white border border-border focus:border-ink outline-none text-sm"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1.5">
              Password
            </label>
            <input
              data-testid="auth-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={mode === "signup" ? 8 : 1}
              className="w-full px-3 py-2.5 bg-white border border-border focus:border-ink outline-none text-sm"
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
            />
            {mode === "signup" && (
              <p className="text-xs text-muted-foreground mt-1.5">Minimum 8 characters.</p>
            )}
          </div>

          {error && (
            <div
              data-testid="auth-error"
              className="p-3 border border-destructive/30 bg-destructive/5 text-destructive text-sm"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="auth-submit-btn"
            className="w-full py-3 bg-gold text-ivory hover:bg-gold/90 disabled:opacity-60 transition-colors text-sm tracking-wide inline-flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === "signup" ? "Create account" : "Sign in"}
          </button>

          <div className="text-xs text-muted-foreground text-center pt-2">
            {mode === "signup" ? (
              <>Already have an account?{" "}
                <button type="button" onClick={() => setMode("login")} className="text-gold underline underline-offset-4 hover:text-gold/80">
                  Sign in
                </button>
              </>
            ) : (
              <>New here?{" "}
                <button type="button" onClick={() => setMode("signup")} className="text-gold underline underline-offset-4 hover:text-gold/80">
                  Create an account
                </button>
              </>
            )}
          </div>

          <div className="text-[11px] text-muted-foreground text-center pt-1 border-t border-border mt-4">
            Demo: <span className="font-mono">demo@elitestock.com / Demo@1234</span>
          </div>
        </form>
      </div>
    </div>
  );
}
